/**
 * <Icon> — the single place the app draws an icon.
 *
 * Replaces the Remix Icon webfont (`<i className="ri-map-pin-line text-[15px]" />`)
 * with lucide-react SVGs, WITHOUT forcing every one of the ~2,400 call sites to
 * move its size and colour out of Tailwind and into props.
 *
 * It does that by defaulting to `size="1em"` and `color="currentColor"`, which
 * is exactly how the webfont behaved: the glyph took its size from font-size
 * and its colour from the inherited text colour. So an existing
 *
 *     <i className="ri-map-pin-line text-[15px] text-[#5a5d77]" />
 *
 * becomes
 *
 *     <Icon name="ri-map-pin-line" className="text-[15px] text-[#5a5d77]" />
 *
 * and renders at the same size in the same colour. Pass an explicit `size` when
 * you want a real pixel value instead — new code should prefer that.
 *
 * Sizing note: because `size="1em"` resolves against font-size, an icon inside
 * a flex row with no font-size of its own inherits the parent's. That matches
 * the font's old behaviour exactly, including in the places that relied on it.
 */
import type { ComponentType, SVGProps } from "react";
import { ICON_MAP } from "./map";

export type IconProps = Omit<SVGProps<SVGSVGElement>, "name" | "color"> & {
  /** The legacy Remix class name, e.g. "ri-map-pin-line". */
  name: string;
  /** Defaults to "1em" so Tailwind text-[Npx] keeps controlling the size. */
  size?: number | string;
  /** Defaults to currentColor so Tailwind text-[#hex] keeps controlling colour. */
  color?: string;
  strokeWidth?: number | string;
  /** Tooltip text. On the old <i> this was the HTML title attribute; on an SVG
   *  the equivalent is a <title> child, which is also what screen readers read.
   *  Supplying it therefore un-hides the icon from the accessibility tree. */
  title?: string;
};

/** Icons Remix drew as a solid glyph render filled rather than outlined.
 *  Lucide ships one outline shape per icon, so a filled star has to be the
 *  same shape with fill applied — otherwise every rating star, ticket and
 *  map pin silently becomes a hollow outline. */
function fillProps(filled: boolean, color: string) {
  return filled ? { fill: color, stroke: color, strokeWidth: 0.5 } : {};
}

/** `name` may carry extra Tailwind classes alongside the icon, because plenty
 *  of call sites compute the whole className at runtime:
 *
 *      <i className={isRtl ? "ri-arrow-right-s-line text-[22px]" : "ri-arrow-left-s-line text-[22px]"} />
 *
 *  Splitting here means those convert to <Icon name={sameExpression} /> with no
 *  restructuring — the leftover classes are merged into className. */
function splitName(raw: string): { icon: string; extra: string } {
  if (!raw.includes(" ")) return { icon: raw, extra: "" };
  const parts = raw.split(/\s+/).filter(Boolean);
  const i = parts.findIndex((p) => p.startsWith("ri-"));
  if (i === -1) return { icon: raw, extra: "" };
  return { icon: parts[i], extra: parts.filter((_, k) => k !== i).join(" ") };
}

export function Icon({
  name,
  size = "1em",
  color = "currentColor",
  strokeWidth,
  className,
  title,
  ...rest
}: IconProps) {
  const { icon, extra } = splitName(name);
  const mergedClass = [extra, className].filter(Boolean).join(" ") || undefined;
  const entry = ICON_MAP[icon];

  if (!entry) {
    /* Unknown name renders nothing rather than throwing — a bad icon must not
       take down a page. In dev it complains loudly, because silently drawing
       nothing is the exact failure mode the old font subset had. */
    if (import.meta.env?.DEV) {
      console.warn(`[icons] unknown icon "${icon}" — add it to packages/icons`);
    }
    return null;
  }

  const C = entry.C as ComponentType<Record<string, unknown>>;

  return (
    <C
      size={size}
      color={color}
      className={mergedClass}
      {...(strokeWidth !== undefined ? { strokeWidth } : {})}
      {...fillProps(entry.filled, color)}
      aria-hidden={title ? undefined : "true"}
      role={title ? "img" : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
    </C>
  );
}
